import { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import FJLocalStore from '../../../../../store/FJLocalStore';
import FJButton, { FJButtonType } from '../../../../../common/components/FJButton/FJButton';
import FJImage from '../../../../../common/components/FJImage/FJImage';
import classNames from 'classnames';
import FJTextWithEllipsisParent from '../../../../../common/components/FJTextWithEllipsis/FJTextWithEllipsisParent';
import FJUploadIcon from '../../../../../assets/svg/upload-white.svg?react';
import FJDeleteIcon from '../../../../../assets/svg/delete.svg?react';
import FJDeleteBlackIcon from '../../../../../assets/svg/delete-black.svg?react';
import FJReplaceIcon from '../../../../../assets/svg/replace-image.svg?react';
import FJReplaceBlackIcon from '../../../../../assets/svg/replace-image-black.svg?react';
import FJErrorIcon from '../../../../../assets/svg/error-red-circle.svg?react';
import FJConfig from '../../../../../config/FJConfig';
import FJConstant from '../../../../../constants/FJConstant';
import FJToolTip from '../../../../../common/components/FJToolTip/FJToolTip';
import FJFileHandleUtil from '../../../../../util/FJFileHandleUtil';
import FJCropImageIcon from '../../../../../assets/svg/crop-image.svg?react';
import DragReplaceIcon from '../../../../../assets/svg/drag-replace.svg?react';
import { CLOUD_TRANSCODE_SUPPORTED_FILE_TYPE_IMAGES } from '../../../../../config/dataConfig/FJGenerateData';

import styles from './FJImageUploadValidator.module.less';
import commonStyles from './StepForm.module.less';

const BAD_PHOTO_EXAMPLE = [
    {
        id: 1,
        title: 'covered-face',
        url: 'tips/headshot-error_6.png',
    },
    {
        id: 2,
        title: 'too-small-face',
        url: 'tips/headshot-error_5.png',
    },
    {
        id: 3,
        title: 'side-shot',
        url: 'tips/headshot-error_1.png',
    },
    {
        id: 4,
        title: 'low-quality-image',
        url: 'tips/headshot-error_4.png',
    },
    {
        id: 5,
        title: 'multiple-people',
        url: 'tips/headshot-error_3.png',
    },
    {
        id: 6,
        title: 'exaggerated-expressions',
        url: 'tips/headshot-error_2.png',
    },
];

const FJImageUploadValidator = ({
    onImageUpload,
    disabled,
    imageInfo,
    onImageDelete,
    openMediaLibrary,
    imageUploadStatus,
    onReCropImage,
}) => {
    const [isDraggingFile, setIsDraggingFile] = useState(false);
    const inputRef = useRef(null);

    const _onDragOver = e => {
        if (disabled) return;
        e.stopPropagation();
        e.preventDefault();
        setIsDraggingFile(true);
    };

    const _onDragLeave = useCallback(
        e => {
            if (disabled) return;
            e.stopPropagation();
            e.preventDefault();
            setIsDraggingFile(false);
        },
        [disabled],
    );

    const _onDragDrop = useCallback(
        e => {
            if (disabled) return;
            e.stopPropagation();
            e.preventDefault();
            setIsDraggingFile(false);

            let files = Array.from(e.dataTransfer.files);
            if (files && files.length > 0) {
                onImageUpload(files[0]);
            }
        },
        [disabled, onImageUpload],
    );

    const _onImageUpload = useCallback(
        e => {
            const file = e.target.files[0];
            onImageUpload(file);
            e.target.value = null;
        },
        [onImageUpload],
    );

    const _onDeleteImage = useCallback(
        e => {
            e.stopPropagation();
            e.preventDefault();
            onImageDelete();
        },
        [onImageDelete],
    );

    const _onUpload = useCallback(
        e => {
            e.stopPropagation();
            if (disabled) {
                return;
            }

            if (window.isEditPage && openMediaLibrary) {
                openMediaLibrary();
            } else if (inputRef.current) {
                inputRef.current.click();
            }
        },
        [disabled, openMediaLibrary, inputRef],
    );

    const renderImagePreview = useCallback(() => {
        return (
            <>
                <FJImage
                    imgClassName={styles.upload_image}
                    boxClassName={styles.upload_image_box}
                    imgSrc={imageInfo.src}
                />
                {(imageUploadStatus === FJConstant.DEFAULT || imageUploadStatus === FJConstant.SUCCESS) && (
                    <>
                        {isDraggingFile && (
                            <div className={classNames(styles.filter_mask, isDraggingFile ? styles.dragging : '')}>
                                <DragReplaceIcon />
                                <span className={styles.filter_mask_text}>{FJLocalStore._('drag-and-drop')}</span>
                            </div>
                        )}
                        <div className={styles.dragging_mask}></div>
                        <div className={styles.button_box}>
                            <FJToolTip title={FJLocalStore._('delete-image')}>
                                <div className={styles.delete_button} onClick={_onDeleteImage}>
                                    <FJDeleteIcon />
                                </div>
                            </FJToolTip>
                            <FJToolTip title={FJLocalStore._('re-crop-image')}>
                                <div className={styles.re_crop_button} onClick={onReCropImage}>
                                    <FJCropImageIcon />
                                </div>
                            </FJToolTip>
                            <FJToolTip title={FJLocalStore._('replace-image')}>
                                <div className={styles.replace_button} onClick={_onUpload}>
                                    <FJReplaceIcon />
                                </div>
                            </FJToolTip>
                        </div>
                    </>
                )}
                {imageUploadStatus === FJConstant.UPLOADING && (
                    <div className={styles.scanning_box}>
                        <div className={styles.scanning_line}></div>
                    </div>
                )}
                {(imageUploadStatus === FJConstant.ERROR || imageUploadStatus === FJConstant.INVALID) && (
                    <div className={styles.error_button_box}>
                        <FJErrorIcon />
                        <span className={styles.error_text}>
                            {FJLocalStore._(
                                imageUploadStatus === FJConstant.ERROR
                                    ? 'upload-failed-please-try-again-later'
                                    : 'image-not-compliant',
                            )}
                        </span>
                        <FJToolTip title={FJLocalStore._('delete-image')}>
                            <div className={styles.error_delete_button} onClick={_onDeleteImage}>
                                <FJDeleteBlackIcon />
                            </div>
                        </FJToolTip>
                        <FJToolTip title={FJLocalStore._('re-crop-image')}>
                            <div className={styles.error_re_crop_button} onClick={onReCropImage}>
                                <FJCropImageIcon />
                            </div>
                        </FJToolTip>
                        <FJToolTip title={FJLocalStore._('replace-image')}>
                            <div className={styles.error_replace_button} onClick={_onUpload}>
                                <FJReplaceBlackIcon />
                            </div>
                        </FJToolTip>
                    </div>
                )}
            </>
        );
    }, [imageInfo.src, _onDeleteImage, _onUpload, imageUploadStatus, onReCropImage, isDraggingFile]);

    return (
        <div
            className={classNames(commonStyles.step_form, styles.upload_validator)}
            onDragOver={_onDragOver}
            onDrop={_onDragDrop}
            onDragLeave={_onDragLeave}
        >
            <h3 className={commonStyles.step_title}>{FJLocalStore._('upload-photo')}</h3>
            <div className={styles.upload_section}>
                <div
                    data-status={imageUploadStatus}
                    className={classNames(styles.upload_area, isDraggingFile && styles.dragging)}
                >
                    <div className={styles.upload_border_box}></div>
                    {imageInfo.src ? (
                        renderImagePreview()
                    ) : (
                        <>
                            <p className={styles.upload_text}>{FJLocalStore._('upload-or-drag-and-drop-photo')}</p>
                            <p className={styles.upload_description}>
                                {FJLocalStore._('photo-supports-formats').replace('xxx', '500').replace('yyy', '5')}
                            </p>
                            <FJButton
                                type={FJButtonType.GRADIENT}
                                className={styles.upload_button}
                                text={FJLocalStore._('upload')}
                                icon={<FJUploadIcon className={styles.upload_icon} />}
                                onClick={_onUpload}
                            />
                        </>
                    )}
                    <input
                        type='file'
                        accept={CLOUD_TRANSCODE_SUPPORTED_FILE_TYPE_IMAGES}
                        ref={inputRef}
                        className={styles.upload_input}
                        onChange={_onImageUpload}
                    />
                </div>
                <div className={styles.validation_info_section}>
                    <p className={styles.validation_info_title}>{FJLocalStore._('photo-requirements')}</p>
                    <div className={styles.validation_info}>
                        <div className={styles.validation_info_item}>
                            <span className={styles.validation_info_item_title}>
                                {FJLocalStore._('good-photo-example')}
                            </span>
                            <FJImage
                                imgClassName={styles.validation_info_item_image}
                                boxClassName={styles.validation_info_item_box}
                                imgSrc={FJFileHandleUtil.getImageUrlWithFormat(
                                    `${FJConfig.headshotImagePath}tips/headshot-correct.png?v=${FJConfig.version}`,
                                )}
                                isShowLoading
                            />
                            <span className={styles.validation_info_item_description}>
                                {FJLocalStore._('single-person-front-facing-clear-clean-background')}
                            </span>
                        </div>

                        <div className={classNames(styles.validation_info_item, styles.bad_photo_example)}>
                            <span className={styles.validation_info_item_title}>
                                {FJLocalStore._('bad-photo-example')}
                            </span>
                            {BAD_PHOTO_EXAMPLE.map(item => (
                                <div key={item.id}>
                                    <FJImage
                                        key={item.id}
                                        imgClassName={styles.validation_info_item_image}
                                        boxClassName={classNames(styles.validation_info_item_box, styles.bad_photo)}
                                        imgSrc={FJFileHandleUtil.getImageUrlWithFormat(
                                            `${FJConfig.headshotImagePath}${item.url}?v=${FJConfig.version}`,
                                        )}
                                        isShowLoading
                                    ></FJImage>
                                    <FJTextWithEllipsisParent
                                        text={FJLocalStore._(item.title)}
                                        className={styles.image_name}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

FJImageUploadValidator.propTypes = {
    onImageUpload: PropTypes.func,
    onValidationComplete: PropTypes.func,
    disabled: PropTypes.bool,
    imageInfo: PropTypes.object,
    onImageDelete: PropTypes.func,
    openMediaLibrary: PropTypes.func,
    imageUploadStatus: PropTypes.string,
    onReCropImage: PropTypes.func,
};

export default FJImageUploadValidator;
